import { useState } from "react";
import type { Dream } from "@buildlab/codeview";
import { useFeature } from "./features";

// The Impossible List (feature impossibleList): the student's no-limits dream, sorted into four
// columns. Sorting goes through the helper, which writes ## Dream in PROJECT.md, so this panel
// only fills the message box. Students see the same text the helper gets.

const COLUMNS: { key: keyof Omit<Dream, "unsorted">; title: string; hint: string }[] = [
  { key: "today", title: "Today", hint: "small enough to build next" },
  { key: "week", title: "This week", hint: "in the next few days" },
  { key: "someday", title: "Someday", hint: "after the basics work" },
  { key: "grownup", title: "Needs a grown-up's help", hint: "BuildLab can't do this yet" },
];

/** The message the helper gets when a student asks to sort their dream. */
export function sortMessage(wishes: string[]): string {
  return `Here is my dream: ${wishes.join("; ")}. Sort it into Today, This week, Someday and Needs a grown-up's help, and write it under ## Dream in PROJECT.md.`;
}

export function DreamPanel({ dream, onAskHelper }: { dream: Dream; onAskHelper: (text: string) => void }) {
  const on = useFeature("impossibleList");
  const [wish, setWish] = useState("");
  if (!on) return null;

  const wishes = [...COLUMNS.flatMap((c) => dream[c.key].map((i) => i.text)), ...dream.unsorted];

  if (wishes.length === 0) {
    return (
      <section className="panel dream-panel" aria-label="The Impossible List">
        <span className="eyebrow">The Impossible List</span>
        <form
          className="dream-start"
          onSubmit={(e) => {
            e.preventDefault();
            const text = wish.trim();
            if (text) onAskHelper(sortMessage([text]));
          }}
        >
          <label htmlFor="dream-wish">What's the biggest version of this you can imagine?</label>
          <textarea id="dream-wish" rows={2} maxLength={300} value={wish} onChange={(e) => setWish(e.target.value)} />
          <div>
            <button type="submit" className="primary small" disabled={!wish.trim()}>
              Put it in my message
            </button>
          </div>
        </form>
      </section>
    );
  }

  return (
    <section className="panel dream-panel" aria-label="The Impossible List">
      <div className="row between">
        <div>
          <span className="eyebrow">The Impossible List · dream big, then shrink to the first step</span>
        </div>
        <button type="button" className="primary small" onClick={() => onAskHelper(sortMessage(wishes))}>
          Sort my dream
        </button>
      </div>
      <div className="dream-cols">
        {COLUMNS.map((c) => (
          <section key={c.key} className={`dream-col k-${c.key}`} aria-label={c.title}>
            <b>{c.title}</b>
            <span className="muted small">{c.hint}</span>
            <ul>
              {dream[c.key].map((item, i) => (
                <li key={i} className={item.done ? "done" : undefined}>
                  {item.text}
                </li>
              ))}
              {dream[c.key].length === 0 && <li className="none">Nothing yet</li>}
            </ul>
          </section>
        ))}
      </div>
      {dream.unsorted.length > 0 && <p className="muted small">Not sorted yet: {dream.unsorted.join(" · ")}</p>}
    </section>
  );
}
